import type { JoinTicketRequest } from '../mp/shared'
import { WsMultiplayerApi } from '../mp/client/WsMultiplayerApi'
import type { MultiplayerApi } from '../mp/client/MultiplayerApi'

/**
 * Dependencies for minting an authenticated join ticket against the hosted
 * mp-room-server from the SDK playground (R9(b)). Mirrors the real RUN client's
 * WsMultiplayerApi wiring, but sources auth from the playground (Firebase ID
 * token + game-session token) instead of a native-host RPC.
 */
export interface HostedMultiplayerDeps {
  /** Hosted room-server base URL (no trailing slash), e.g. mp-room-server-playground. */
  roomServerUrl: string
  /** The game whose deterministic room the ticket targets. */
  gameId: string
  /** Version tag the ticket resolves the room config under (playground uploads `private`). */
  versionTag: string
  /** Resolve the caller's Firebase ID token (throws / returns empty when signed out). */
  getIdToken: () => Promise<string>
  /** Resolve the playground game-session token (GST), or null when unavailable. */
  getGst: () => Promise<string | null>
  /** Injected for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch
}

/**
 * Build the `getJoinTicket` callback for a hosted playground WsMultiplayerApi. It
 * POSTs to `${roomServerUrl}/tickets` with the caller's Firebase ID token, the
 * real gameId, and the version tag, attaching the GST header when present. It
 * fails loud: no token → throw before the request; non-OK → throw the server's
 * error (never a silent unauthenticated fallback).
 */
export function buildHostedGetJoinTicket(
  deps: HostedMultiplayerDeps,
): (req: JoinTicketRequest) => Promise<string> {
  const fetchImpl = deps.fetchImpl ?? fetch
  return async (req: JoinTicketRequest): Promise<string> => {
    const token = await deps.getIdToken()
    if (!token) {
      throw new Error(
        '[RUN:playground] Cannot mint a hosted join ticket: no signed-in player (sign in first).',
      )
    }
    const gst = await deps.getGst()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (gst) headers['x-h5-game-session'] = gst

    const resp = await fetchImpl(`${deps.roomServerUrl}/tickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...req, token, gameId: deps.gameId, versionTag: deps.versionTag }),
    })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: 'Ticket request failed' }))
      throw new Error(err.error || 'Ticket request failed')
    }
    const data = await resp.json()
    return data.ticket
  }
}

/**
 * Decide whether the playground host connects to the hosted mp-room-server or
 * falls back to the offline Mock: hosted MP needs both an injected room-server
 * URL AND a version tag (the tag the GST is minted under). Returns the narrowed
 * pair when both are present, else null (Mock path). Pure — the selection the
 * PlaygroundHost constructor makes, without constructing the host.
 */
export function resolveHostedMultiplayerTarget(
  roomServerUrl: string | undefined,
  versionTag: string | undefined,
): { roomServerUrl: string; versionTag: string } | null {
  if (!roomServerUrl || !versionTag) {
    return null
  }
  return { roomServerUrl, versionTag }
}

/**
 * Construct a WsMultiplayerApi that connects the SDK playground to the hosted
 * mp-room-server with authenticated join tickets (R9(b)).
 */
export function createHostedMultiplayerApi(deps: HostedMultiplayerDeps): MultiplayerApi {
  return new WsMultiplayerApi({
    serverUrl: deps.roomServerUrl,
    getJoinTicket: buildHostedGetJoinTicket(deps),
  })
}
